import React from 'react';
import {
    ChevronDown,
    ChevronUp,
    Upload,
    CreditCard,
    Wallet,
    Building2,
    FileText,
    Eye,
    AlertCircle,
    CheckCircle,
    Loader,
    Loader2,
    Info,
    Layers,
    X,
    ShieldAlert,
    Ban,
    Check,
    Plus,
    SaudiRiyal,
} from 'lucide-react';
import { Link, NavLink } from 'react-router-dom';
import swal from 'sweetalert';
import i18n from '../../../../i18n';
import { getLoggedUser } from '../../../../state/user';
import { formatCurrency } from '../../../../state/utils';
import { checkBlackList } from '../../../../models/blacklist';
import { enrollUser, getAuction } from '../../../../models/auctions';
import { getUserInfo } from '../../../../models/users';
import { createPayment, fetchAcknowledgementPdf } from './services/enrollService';
import { WalletFund } from '../../../helpers/walletFund';
import HeaderBanner from '../../../common/HeaderBanner';
import { CustomModal } from '../../../common/CustomModal';
import { openBlobInNewTab } from '../../../../utils/common';
import toast from 'react-hot-toast';
import '../../../../styles/components/enroll-page.scss';

const user = getLoggedUser();

const USER_FIELDS = {
    file_id_number: i18n.t('attach_id_no'),
    authority_name: i18n.t('auth_name'),
    account_number: i18n.t('account_number'),
    cr_number: i18n.t('cr_number'),
    resident_card_number: i18n.t('personal_card_no'),
    vat_number: i18n.t('vat_number'),
    file_cr_number: i18n.t('attacth_cr_copy'),
    file_auth_letter: i18n.t('auth_letter'),
    file_vat_certificate: i18n.t('attach_vat'),
};

export class AuctionEnrollment extends React.Component {
    constructor() {
        super();
        let path = window.location.pathname;
        let auctionId = path.substring(path.lastIndexOf('/') + 1, path.length);
        this.state = {
            online_type: 'local',
            show_terms_panel: false,
            show_bank_panel: true,
            ptype: 'offline',
            input: { ...user },
            is_company: false,
            t_c_accept: false,
            rulesAccepted: false,
            showAcknowledgementModal: false,
            isAcknowledgementLoading: false,
            payment: {},
            auction: auctionId,
            is_payment: false,
            file_receipt: null,
            receiptPreviewUrl: null,
            is_payment_loading: false,
            is_loading: false,
            show_success_message: false,
            terms: '',
            terms_ar: '',
            is_online_payment_needed: false,
            is_online_payment_mandatory: false,
            need_online_payment: false,
            enrollData: [],
            selected_price: 20000,
            show_payment_details: false,
            auction_details: {},
            is_not_wallet_balance: false,
            is_profile_completed: true,
            wallet_amount: 0,
            is_blacklisted: false,
            missingField: [],
            show_wallet_fund_screen: false,
        };
        this.handleChange = this.handleChange.bind(this);
        this.handlePayment = this.handlePayment.bind(this);
        this.onFileChange = this.onFileChange.bind(this);
        this.saveEnroll = this.saveEnroll.bind(this);
    }

    // ─── Lifecycle ─────────────────────────────────────────────

    componentDidMount() {
        window.scrollTo(0, 0);
        this.loadAuction();
        this.checkUserWallet();
        this.checkBlacklist();
    }

    componentWillUnmount() {
        if (this.state.receiptPreviewUrl) {
            URL.revokeObjectURL(this.state.receiptPreviewUrl);
        }
    }

    componentDidCatch(error, errorInfo) {
        console.error('Enrollment Error:', error, errorInfo);
        this.setState({ hasError: true });
    }

    // ─── Data Loaders ──────────────────────────────────────────

    async checkBlacklist() {
        const response = await checkBlackList();
        if (response.success) {
            let { data } = response;
            this.setState({ is_blacklisted: data.is_blacklisted });
        }
    }

    async loadAuction() {
        let id = this.state.auction;
        const response = await getAuction(id);
        if (response) {
            let auction = response.data;
            this.setState({ auction_details: auction });
            let payment_needed = false;
            if (auction.payment_type === 'Online' || auction.payment_type === 'Both') {
                payment_needed = true;
                if (auction.payment_type === 'Online') {
                    this.setState({
                        is_online_payment_mandatory: true,
                        show_payment_details: true,
                        need_online_payment: true,
                        ptype: 'online',
                    });
                }
                if (auction.payment_amount) {
                    let paymentAmount = auction.payment_amount * 1000;
                    this.setState({ selected_price: paymentAmount });
                }
            }
            this.setState({
                terms: auction.terms,
                terms_ar: auction.terms_arabic,
                is_online_payment_needed: payment_needed,
            });
        }
    }

    async checkUserWallet() {
        const response = await getUserInfo(user.id);
        if (response && response.success) {
            let usr = response.data;
            const profile = response.meta?.profile;
            if (usr) {
                user.wallet_amount = usr.wallet_amount;
                localStorage.setItem('mzad_user_info', JSON.stringify(user));
                this.setState({
                    wallet_amount: usr.wallet_amount,
                    is_profile_completed: !profile?.update_required,
                    missingField: profile?.missing_fields ?? [],
                });
                if (
                    Number(usr.wallet_amount) === 0 ||
                    Number(usr.wallet_amount) < Number(this.state.auction_details.payment_amount)
                ) {
                    this.setState({ is_not_wallet_balance: true });
                }
            }
        }
    }

    // ─── Event Handlers ────────────────────────────────────────

    handleChange(event) {
        let input = this.state.input;
        input[event.target.name] = event.target.value;
        this.setState({ input });
    }

    handlePayment(event) {
        let payment = this.state.payment;
        payment[event.target.name] = event.target.value;
        this.setState({ payment });
    }

    onFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!this.validateFileSize(file)) {
            swal('Failed', 'File size should be less than 5MB', 'error');
            return;
        }

        if (event.target.name === 'file_receipt') {
            if (this.state.receiptPreviewUrl) {
                URL.revokeObjectURL(this.state.receiptPreviewUrl);
            }
            this.setState({
                file_receipt: file,
                receiptPreviewUrl: URL.createObjectURL(file),
            });
        }
    };

    validateFileSize(file) {
        const MAX_FILE_SIZE = 5120; // 5MB
        const fileSizeKiloBytes = file.size / 1024;
        if (fileSizeKiloBytes > MAX_FILE_SIZE) {
            return;
        }
        return true;
    }

    removeReceiptFile = () => {
        if (this.state.receiptPreviewUrl) {
            URL.revokeObjectURL(this.state.receiptPreviewUrl);
        }
        this.setState({ file_receipt: null, receiptPreviewUrl: null });
    };

    openReceiptPreview = () => {
        if (this.state.receiptPreviewUrl) {
            window.open(this.state.receiptPreviewUrl, '_blank');
        }
    };

    formatFileSize(bytes) {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
        return (bytes / 1048576).toFixed(1) + ' MB';
    }

    // ─── Enroll / Payment ──────────────────────────────────────

    async saveEnroll(event) {
        event.preventDefault();
        // Validation
        let { bank, account_number, beneficiary, receipt_number } = this.state.input;
        if (this.state.ptype === 'offline') {
            if (
                !bank ||
                bank === 'null' ||
                !account_number ||
                account_number === 'null' ||
                !beneficiary ||
                beneficiary === 'null' ||
                !receipt_number ||
                receipt_number === 'null' ||
                this.state.file_receipt === null
            ) {
                swal(i18n.t('failed'), i18n.t('all_mandatory'), 'error');
                return;
            }
        }

        let { auction_details } = this.state;
        this.setState({ is_loading: true });
        let amount = auction_details.payment_amount;
        if (this.state.ptype === 'online') {
            let { online_type } = this.state;
            let two_percent = (parseFloat(amount) * 2) / 100;
            let credit_amount = parseFloat(amount) + parseFloat(two_percent);

            let one_five_percent = (parseFloat(amount) * 1.5) / 100;
            let debit_amount = parseFloat(amount) + parseFloat(one_five_percent);
            if (online_type === 'local') {
                amount = debit_amount;
            }
            if (online_type === 'credit') {
                amount = credit_amount;
            }
        }
        this.setState({ selected_price: amount * 1000 });

        const formData = new FormData();
        formData.append('auction', this.state.auction);
        formData.append('enroll_name', this.state.input.name);
        formData.append('identity_type', this.state.input.identity_type);
        formData.append('bank', this.state.input.bank);
        formData.append('account_number', this.state.input.account_number);
        formData.append('beneficiary', this.state.input.beneficiary);
        formData.append('receipt_number', this.state.input.receipt_number);
        formData.append('is_company', this.state.is_company ? true : false);
        formData.append('is_offline', !this.state.need_online_payment);
        formData.append('ptype', this.state.ptype);
        formData.append('identity_type', 'Civil Card');
        formData.append('amount', auction_details.payment_amount);

        if (this.state.file_receipt) {
            formData.append('file_receipt', this.state.file_receipt, this.state.file_receipt.name);
        }

        this.setState({ is_payment_loading: true });
        const response = await enrollUser(formData);
        if (response.success) {
            this.setState({
                show_success_message: true,
                is_loading: false,
                is_payment_loading: false,
            });
            if (this.state.ptype === 'online') {
                this.setState({ show_success_message: false });
                this.handlePay(response.data);
                this.setState({ is_payment: true, enrollData: response.data });
            }
        } else {
            this.setState({ is_loading: false, is_payment_loading: false });
            swal('Failed', response.message, 'error');
            return;
        }
    }

    handlePay = async (res) => {
        let { auction_details } = this.state;
        this.setState({ is_loading: true });
        let amount = auction_details.payment_amount;
        let og_amount = auction_details.payment_amount;
        if (this.state.ptype === 'online') {
            let { online_type } = this.state;
            let two_percent = (parseFloat(amount) * 2) / 100;
            let credit_amount = parseFloat(amount) + parseFloat(two_percent);

            let one_five_percent = (parseFloat(amount) * 1.5) / 100;
            let debit_amount = parseFloat(amount) + parseFloat(one_five_percent);
            if (online_type === 'local') {
                amount = debit_amount;
            }
            if (online_type === 'credit') {
                amount = credit_amount;
            }
        }
        const data = {
            amount,
            currency: 'SAR',
            description: 'Test Data',
            type: 'enroll',
            group_id: res.group_id,
            auction: res.aution,
            enroll_id: res.id,
            og_amount: og_amount,
        };

        const response = await createPayment(data);
        window.location.href = response.data.redirect_url; // redirect to EdfaPay
        this.setState({ is_loading: false });
    };

    // ─── Render helpers ────────────────────────────────────────

    get isSubmitDisabled() {
        const {
            t_c_accept,
            rulesAccepted,
            is_loading,
            is_profile_completed,
            ptype,
            is_not_wallet_balance,
            is_blacklisted,
            auction_details,
        } = this.state;

        return (
            !t_c_accept ||
            !rulesAccepted ||
            is_loading ||
            !is_profile_completed ||
            (ptype === 'wallet' && is_not_wallet_balance) ||
            auction_details.is_enrolled ||
            is_blacklisted
        );
    }

    get localizedAuctionTitle() {
        const lang = localStorage.getItem('I18N_LANGUAGE');
        const { auction_details } = this.state;
        return lang === 'ar'
            ? auction_details.title_ar || auction_details.title
            : auction_details.title;
    }

    get localizedGroupName() {
        const lang = localStorage.getItem('I18N_LANGUAGE');
        const groupInfo = this.state.auction_details?.group_info;
        if (!groupInfo) return null;
        return lang === 'ar'
            ? groupInfo.group_name_ar || groupInfo.group_name
            : groupInfo.group_name || groupInfo.group_name_ar;
    }

    handleAcknowledgementView = async () => {
        const { auction_details } = this.state;
        const lang = localStorage.getItem('I18N_LANGUAGE') ?? 'en';

        this.setState({ isAcknowledgementLoading: true });
        try {
            const blob = await fetchAcknowledgementPdf(auction_details.id, lang);
            openBlobInNewTab(blob);
        } catch (error) {
            console.error('[AcknowledgementView]', error);
            toast.error(
                i18n.t('acknowledgement_pdf_unavailable') || 'Acknowledgement PDF unavailable.'
            );
        } finally {
            this.setState({ isAcknowledgementLoading: false });
        }
    };

    renderAuctionInfoCard() {
        const { auction_details } = this.state;
        return (
            <div className="mze-panel">
                <div className="mze-panel__header">
                    <Info size={16} />
                    <span>{i18n.t('auction_details') || 'Auction Details'}</span>
                </div>
                <div className="mze-panel__body">
                    <div className="mze-auction-info">
                        <div className="mze-auction-info__left">
                            <span className="mze-auction-info__id">MZAD-{auction_details.id}</span>
                            <h3 className="mze-auction-info__title">{this.localizedAuctionTitle}</h3>
                            {this.localizedGroupName && (
                                <span className="mze-auction-info__group">
                                    <Layers size={12} />
                                    {this.localizedGroupName}
                                </span>
                            )}
                        </div>
                        {auction_details.payment_amount && (
                            <div className="mze-auction-info__right">
                                <span className="mze-auction-info__label">
                                    {i18n.t('enroll_amount')}
                                </span>
                                <span className="mze-auction-info__amount">
                                    {formatCurrency(auction_details.payment_amount)}{' '}
                                    <SaudiRiyal size={16} />
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    renderAlerts() {
        const { is_blacklisted, is_profile_completed, missingField, auction_details } = this.state;
        return (
            <>
                {is_blacklisted && (
                    <div className="mze-alert mze-alert--danger">
                        <Ban size={18} className="mze-alert__icon" />
                        <div className="mze-alert__content">
                            <strong>{i18n.t('blocked') || 'Blocked'}</strong>
                            <p>{i18n.t('user_blacklisted_message')}</p>
                        </div>
                    </div>
                )}

                {!is_profile_completed && (
                    <div className="mze-alert mze-alert--warning">
                        <ShieldAlert size={18} className="mze-alert__icon" />
                        <div className="mze-alert__content">
                            <strong>{i18n.t('profile_incomplete') || 'Profile Incomplete'}</strong>
                            <p>{i18n.t('profile_completion_message_auction')}</p>
                            <div className="mze-alert__badges">
                                {missingField.map((fieldKey, index) => (
                                    <span key={index} className="mze-missing-badge">
                                        {USER_FIELDS[fieldKey]}
                                    </span>
                                ))}
                            </div>
                            <Link to="/profile" className="mze-alert__link">
                                {i18n.t('complete_profile') || 'Complete Profile'} →
                            </Link>
                        </div>
                    </div>
                )}

                {auction_details.is_enrolled && (
                    <div className="mze-alert mze-alert--success">
                        <CheckCircle size={18} className="mze-alert__icon" />
                        <div className="mze-alert__content">
                            <strong>{i18n.t('enrolled') || 'Enrolled'}</strong>
                            <p>{i18n.t('enrolled_message')}</p>
                            <Link to="/" className="mze-alert__link mze-alert__link--success">
                                {i18n.t('home')} →
                            </Link>
                        </div>
                    </div>
                )}
            </>
        );
    }

    renderPaymentTypeTabs() {
        const { ptype } = this.state;
        const tabs = [
            { key: 'offline', icon: <Building2 size={16} />, label: i18n.t('bank_transfer') },
            { key: 'online', icon: <CreditCard size={16} />, label: i18n.t('online') },
            { key: 'wallet', icon: <Wallet size={16} />, label: i18n.t('wallet') },
        ];

        return (
            <div className="mze-tabs">
                {tabs.map((tab) => (
                    <button
                        key={tab.key}
                        className={`mze-tab ${ptype === tab.key ? 'mze-tab--active' : ''}`}
                        onClick={() =>
                            this.setState({ ptype: tab.key, show_wallet_fund_screen: false })
                        }
                    >
                        {tab.icon}
                        <span>{tab.label}</span>
                    </button>
                ))}
            </div>
        );
    }

    renderOfflinePaymentForm() {
        const { input, file_receipt, receiptPreviewUrl, is_profile_completed } = this.state;
        const isPdf = file_receipt?.type === 'application/pdf';
        const isImage = file_receipt?.type?.startsWith('image/');

        const fields = [
            { name: 'bank', label: i18n.t('bank'), placeholder: 'Enter bank name' },
            {
                name: 'account_number',
                label: i18n.t('account_number'),
                placeholder: 'Enter account number',
            },
            {
                name: 'beneficiary',
                label: i18n.t('beneficiary'),
                placeholder: 'Enter beneficiary name',
                full: true,
            },
            {
                name: 'receipt_number',
                label: i18n.t('receipt'),
                placeholder: 'Enter receipt number',
            },
        ];

        return (
            <div className="mze-form-block">
                <div className="mze-policy">
                    <strong className="mze-policy__title">
                        {i18n.t('guarantee_amount_policy_head')}
                    </strong>
                    <div className="mze-policy__text">
                        <p>1) Those willing to participate in the e-auction must deposit a Guarantee amount.</p>
                        <p>2) The Guarantee amount is refundable. However, the Service Charges for the Online Payment is not refundable.</p>
                        <p>3) The guarantee amount is separate from the value of the items offered in the auction.</p>
                        <p>4) The Guarantee amount may vary from item to item.</p>
                        <p>5) The Guarantee amount is not automatically returned. In case of delay, Mzadcom will not bear any delay in refund.</p>
                    </div>
                </div>

                {/* ── Receipt Upload ── */}
                <div className="mze-field mze-field--upload">
                    <label className="mze-field__label">
                        <Upload size={14} />
                        {i18n.t('receipt')}
                        <span className="mze-field__required">*</span>
                    </label>

                    <input
                        type="file"
                        id="file_receipt"
                        name="file_receipt"
                        accept=".jpg,.jpeg,.png,.pdf"
                        style={{ display: 'none' }}
                        disabled={!is_profile_completed}
                        onChange={this.onFileChange}
                    />

                    {!file_receipt ? (
                        <div className={`mze-upload-zone ${!is_profile_completed ? 'mze-upload-zone--disabled' : ''}`}>
                            <div className="mze-upload-zone__icon">
                                <Upload size={24} />
                            </div>
                            <p className="mze-upload-zone__title">
                                {i18n.t('drop_receipt_here') || 'Upload your receipt'}
                            </p>
                            <p className="mze-upload-zone__sub">JPG, PNG or PDF — max 5 MB</p>
                            <button
                                type="button"
                                className="mze-upload-zone__btn"
                                disabled={!is_profile_completed}
                                onClick={() => document.getElementById('file_receipt').click()}
                            >
                                {i18n.t('browse_file') || 'Browse file'}
                            </button>
                        </div>
                    ) : (
                        <div className="mze-receipt-preview">
                            <div className="mze-receipt-preview__header">
                                <FileText size={15} className="mze-receipt-preview__file-icon" />
                                <span className="mze-receipt-preview__name">{file_receipt.name}</span>
                                <span className="mze-receipt-preview__size">
                                    {this.formatFileSize(file_receipt.size)}
                                </span>
                                <button
                                    type="button"
                                    className="mze-receipt-preview__remove"
                                    onClick={this.removeReceiptFile}
                                >
                                    <X size={12} /> {i18n.t('remove') || 'Remove'}
                                </button>
                            </div>

                            <div className={`mze-receipt-preview__body ${isImage ? 'mze-receipt-preview__body--image' : ''}`}>
                                {isImage && (
                                    <img
                                        src={receiptPreviewUrl}
                                        alt="Receipt preview"
                                        className="mze-receipt-preview__img"
                                    />
                                )}
                                {isPdf && (
                                    <div className="mze-receipt-preview__pdf">
                                        <FileText size={32} className="mze-receipt-preview__pdf-icon" />
                                        <div>
                                            <p className="mze-receipt-preview__pdf-name">{file_receipt.name}</p>
                                            <span className="mze-receipt-preview__pdf-meta">
                                                PDF document · {this.formatFileSize(file_receipt.size)}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mze-receipt-preview__actions">
                                <button
                                    type="button"
                                    className="mze-receipt-preview__view-btn"
                                    onClick={this.openReceiptPreview}
                                >
                                    <FileText size={13} /> {i18n.t('view_full_size') || 'View full size'}
                                </button>
                                <button
                                    type="button"
                                    className="mze-receipt-preview__change-btn"
                                    onClick={() => document.getElementById('file_receipt').click()}
                                >
                                    <Upload size={13} /> {i18n.t('change_file') || 'Change file'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Bank Fields ── */}
                <div className="mze-offline-fields">
                    {fields.map((field) => (
                        <div
                            className={`mze-field ${field.full ? 'mze-field--full' : ''}`}
                            key={field.name}
                        >
                            <label className="mze-field__label">
                                <span className="mze-field__required">*</span>
                                {field.label}
                            </label>
                            <input
                                className="mze-field__input"
                                type="text"
                                name={field.name}
                                placeholder={field.placeholder}
                                value={input[field.name] || ''}
                                onChange={this.handleChange}
                            />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    renderOnlinePaymentForm() {
        const { online_type, auction_details } = this.state;
        const payment_amount = parseFloat(auction_details.payment_amount) || 0;
        const debit_amount = payment_amount + (payment_amount * 1.5) / 100;
        const credit_amount = payment_amount + (payment_amount * 2) / 100;

        return (
            <div className="mze-form-block">
                <div className="mze-policy">
                    <strong className="mze-policy__title">Online Payment Policy</strong>
                    <div className="mze-policy__text">
                        <p>1) Service charges apply for online payments.</p>
                        <p>2) 1.5% for local/debit cards, 2% for credit cards.</p>
                        <p>3) Service charges are non-refundable.</p>
                        <p>4) You will be redirected to EDFA Payment Gateway.</p>
                    </div>
                </div>

                <p className="mze-thawani__text">{i18n.t('redirect_to_payment')}</p>

                <div className="mze-card-types">
                    <button
                        className={`mze-card-type ${online_type === 'local' ? 'mze-card-type--active' : ''}`}
                        onClick={() => this.setState({ online_type: 'local' })}
                    >
                        <div>{i18n.t('local_cards') || 'Local Cards'} (1.5%)</div>
                        <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                            {debit_amount.toFixed(2)} <SaudiRiyal size={13} />
                        </div>
                    </button>
                    <button
                        className={`mze-card-type ${online_type === 'credit' ? 'mze-card-type--active' : ''}`}
                        onClick={() => this.setState({ online_type: 'credit' })}
                    >
                        <div>{i18n.t('credit_cards') || 'Credit Cards'} (2%)</div>
                        <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                            {credit_amount.toFixed(2)} <SaudiRiyal size={13} />
                        </div>
                    </button>
                </div>
            </div>
        );
    }

    renderWalletPaymentForm() {
        const { wallet_amount, is_not_wallet_balance, show_wallet_fund_screen } = this.state;
        return (
            <div className="mze-form-block">
                <div className="mze-wallet-bar">
                    <Wallet size={18} className="mze-wallet-bar__icon" />
                    <span className="mze-wallet-bar__text">
                        {i18n.t('balance')}:
                        <strong className="mze-wallet-bar__amount">
                            {wallet_amount} <SaudiRiyal size={14} />
                        </strong>
                    </span>
                </div>

                {is_not_wallet_balance && (
                    <>
                        <div className="mze-alert mze-alert--danger mze-alert--compact">
                            <AlertCircle size={16} className="mze-alert__icon" />
                            <span>{i18n.t('insufficient_wallet_balance')}</span>
                        </div>
                        <button
                            type="button"
                            className="mze-add-fund"
                            onClick={() =>
                                this.setState({ show_wallet_fund_screen: !show_wallet_fund_screen })
                            }
                        >
                            <Plus size={18} />
                            <span>{i18n.t('add_fund')}</span>
                        </button>
                        {show_wallet_fund_screen && (
                            <div className="mt-3">
                                <WalletFund />
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    }

    renderSubmitSection() {
        const {
            t_c_accept,
            rulesAccepted,
            is_loading,
            show_success_message,
            ptype,
            is_not_wallet_balance,
            isAcknowledgementLoading,
            auction_details,
        } = this.state;

        // Match Saudi behaviour: hide submit when wallet has no balance
        if (ptype === 'wallet' && is_not_wallet_balance) return null;

        return (
            <div className="mze-submit-area">
                {/* ── Terms & Conditions ── */}
                <div className="mze-rules-row">
                    <label className="mze-check">
                        <input
                            type="checkbox"
                            checked={t_c_accept}
                            onChange={() => this.setState({ t_c_accept: !t_c_accept })}
                        />
                        <span className="mze-check__box">
                            {t_c_accept && <Check size={12} strokeWidth={3} />}
                        </span>
                        <span className="mze-check__text">
                            {i18n.t('agree_to_terms')} <span className="mze-field__required">*</span>
                        </span>
                    </label>

                    <div className="mze-rules-actions">
                        <button
                            type="button"
                            className="mze-rules-btn"
                            onClick={() => {
                                if (!auction_details.file_terms) {
                                    toast.error('No files found. Contact admin.');
                                    return;
                                }
                                window.open(auction_details.file_terms, '_blank');
                            }}
                        >
                            <Eye size={14} />
                            <span>{i18n.t('preview') || 'Preview'}</span>
                        </button>
                    </div>
                </div>

                {/* ── Acknowledge auction rules ── */}
                <div className="mze-rules-row">
                    <label
                        className="mze-check"
                        onClick={(e) => {
                            e.preventDefault();
                            if (!rulesAccepted) {
                                this.setState({ showAcknowledgementModal: true });
                            } else {
                                this.setState({ rulesAccepted: false });
                            }
                        }}
                    >
                        <input type="checkbox" checked={rulesAccepted} onChange={() => { }} />
                        <span className="mze-check__box">
                            {rulesAccepted && <Check size={12} strokeWidth={3} />}
                        </span>
                        <span className="mze-check__text">
                            {i18n.t('acknowledge_auction_rules') || 'I acknowledge the auction rules'}
                            <span className="mze-field__required">*</span>
                        </span>
                    </label>

                    {rulesAccepted && (
                        <div className="mze-rules-actions">
                            <button
                                type="button"
                                className="mze-rules-btn"
                                onClick={this.handleAcknowledgementView}
                                disabled={isAcknowledgementLoading}
                            >
                                {isAcknowledgementLoading ? (
                                    <Loader2 size={16} className="mze-submit__spinner" />
                                ) : (
                                    <Eye size={14} />
                                )}
                                <span>{i18n.t('preview') || 'Preview'}</span>
                            </button>
                        </div>
                    )}
                </div>

                <button
                    type="button"
                    className="mze-submit"
                    onClick={this.saveEnroll}
                    disabled={this.isSubmitDisabled}
                >
                    {is_loading ? (
                        <>
                            <Loader size={16} className="mze-submit__spinner" /> Loading...
                        </>
                    ) : (
                        <>
                            <CreditCard size={16} /> {i18n.t('save_continue')}
                        </>
                    )}
                </button>

                {show_success_message && (
                    <div className="mze-alert mze-alert--success mze-alert--compact">
                        <CheckCircle size={18} className="mze-alert__icon" />
                        <div className="mze-alert__content">Enrollment completed successfully!</div>
                    </div>
                )}
            </div>
        );
    }

    renderPaymentPanel() {
        const { ptype } = this.state;
        return (
            <div className="mze-panel">
                <div className="mze-panel__header">
                    <Wallet size={16} />
                    <span>{i18n.t('pay_type') || 'Payment Method'}</span>
                </div>
                <div className="mze-panel__body">
                    {this.renderPaymentTypeTabs()}
                    {ptype === 'offline' && this.renderOfflinePaymentForm()}
                    {ptype === 'online' && this.renderOnlinePaymentForm()}
                    {ptype === 'wallet' && this.renderWalletPaymentForm()}
                    {this.renderSubmitSection()}
                </div>
            </div>
        );
    }

    renderTermsPanel() {
        const { show_terms_panel, auction_details } = this.state;
        return (
            <div className="mze-panel mze-panel--collapsible">
                <button
                    className="mze-panel__toggle"
                    onClick={() => this.setState({ show_terms_panel: !show_terms_panel })}
                >
                    <span className="mze-panel__toggle-left">
                        <FileText size={16} />
                        {i18n.t('terms_conditions')}
                    </span>
                    {show_terms_panel ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>

                {show_terms_panel && (
                    <div className="mze-panel__body">
                        <div className="mze-rules-row">
                            <span className="mze-panel__toggle-left">
                                <FileText size={16} />
                                {i18n.t('download_attatchment')}
                            </span>
                            <button
                                type="button"
                                className="mze-rules-btn"
                                onClick={() => {
                                    if (!auction_details.file_terms) {
                                        swal('Failed', 'No files found. Contact admin.', 'error');
                                        return;
                                    }
                                    window.open(auction_details.file_terms, '_blank');
                                }}
                            >
                                <Eye size={14} />
                                <span>{i18n.t('preview') || 'Preview'}</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    renderBankDetailsPanel() {
        const { show_bank_panel } = this.state;
        return (
            <div className="mze-panel mze-panel--collapsible">
                <button
                    className="mze-panel__toggle"
                    onClick={() => this.setState({ show_bank_panel: !show_bank_panel })}
                >
                    <span className="mze-panel__toggle-left">
                        <Building2 size={16} />
                        {i18n.t('bank_details')}
                    </span>
                    {show_bank_panel ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>

                {show_bank_panel && (
                    <div className="mze-panel__body">
                        <div className="mze-bank-card">
                            <p>Bank Name : Al Rajhi Bank</p>
                            <p>Account Name : Mazadcom Smart Company Auction Solutions SOL</p>
                            <p>Account Number : 465000010006085776030</p>
                            <p>IBAN Number : SA4480000465608015776030</p>
                            <p>Currency : SAUDI RIYAL</p>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    renderAcknowledgementModal() {
        const { showAcknowledgementModal, auction_details } = this.state;
        const clientName = auction_details?.organizationDetails?.organization_name || '—';
        const groupName = this.localizedGroupName || '—';
        const terms = [i18n.t('ack_term_1'), i18n.t('ack_term_2'), i18n.t('ack_term_3')];

        const footer = (
            <div className="mze-ack-footer">
                <button
                    type="button"
                    className="modal-btn btn-cancel"
                    onClick={() =>
                        this.setState({ showAcknowledgementModal: false, rulesAccepted: false })
                    }
                >
                    <X size={14} />
                    <span>{i18n.t('decline')}</span>
                </button>
                <button
                    type="button"
                    className="modal-btn btn-confirm"
                    onClick={() =>
                        this.setState({ showAcknowledgementModal: false, rulesAccepted: true })
                    }
                >
                    <Check size={14} />
                    <span>{i18n.t('accept_confirm')}</span>
                </button>
            </div>
        );

        return (
            <CustomModal
                isOpen={showAcknowledgementModal}
                header={i18n.t('auction_agreement_acknowledgement')}
                headerIcon={<FileText size={18} />}
                footer={footer}
                modalClassName="mze-ack-modal"
                closeBtn={false}
            >
                {/* ── Enrollment confirmation ── */}
                <div className="mze-ack-block">
                    <div className="mze-ack-block__head">
                        <FileText size={13} />
                        <span>{i18n.t('enrollment_confirmation')}</span>
                    </div>
                    <div className="mze-ack-block__body">
                        <p className="mze-ack-intro">
                            {i18n.t('ack_intro') ||
                                'I hereby confirm that I have successfully enrolled for the following auction:'}
                        </p>
                        <div className="mze-ack-table">
                            <div className="mze-ack-row">
                                <span className="mze-ack-row__key">{i18n.t('client_name')}</span>
                                <span className="mze-ack-row__val">{clientName}</span>
                            </div>
                            <div className="mze-ack-row">
                                <span className="mze-ack-row__key">{i18n.t('group_name')}</span>
                                <span className="mze-ack-row__val">{groupName}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Terms ── */}
                <div className="mze-ack-block">
                    <div className="mze-ack-block__head">
                        <FileText size={13} />
                        <span>{i18n.t('acknowledgement_of_terms')}</span>
                    </div>
                    <div className="mze-ack-block__body mze-ack-block__body--terms">
                        {terms.map((term, idx) => (
                            <div key={idx} className="mze-ack-term">
                                <div className="mze-ack-term__num">{idx + 1}</div>
                                <p className="mze-ack-term__text">{term}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Legal notice ── */}
                <div className="mze-ack-notice">
                    <AlertCircle size={14} />
                    <p>{i18n.t('ack_legal_notice')}</p>
                </div>
            </CustomModal>
        );
    }

    // ─── Main Render ───────────────────────────────────────────

    render() {
        return (
            <React.Fragment>
                <div className="mze-page">
                    <HeaderBanner
                        title={i18n.t('auction_enrollment_payments')}
                        subtitle={i18n.t('manage_and_view_enrollment_payments')}
                    />
                    <div className="mze-container">
                        <div className="mze-grid">
                            <div className="mze-left">
                                {this.renderAuctionInfoCard()}
                                {this.renderAlerts()}
                                {this.renderPaymentPanel()}
                            </div>

                            <div className="mze-right">
                                {this.renderTermsPanel()}
                                {this.renderBankDetailsPanel()}
                            </div>
                        </div>
                    </div>
                </div>
                {this.renderAcknowledgementModal()}
            </React.Fragment>
        );
    }
}